import axios, { Canceler } from 'axios';
import moment from 'moment';

import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { ThunkDispatch } from 'redux-thunk';
import { Action } from 'redux';
import { isEmpty } from 'lodash';
import { GetPreferenceValue } from '@utils';
import { IAccount } from '@share/common-types';
import {
  IHotel,
  ILocation,
  IRoom,
  IHotelsResponse,
  ICounters,
  ISessionKey,
  IBounds,
  IHotelsRecentSearches,
  IClientCash,
} from '@share/common-types';
import {
  DEFAULT_PAGE_NUMBER,
  DEFAULT_MAP_LIMIT,
  Urls,
  SESSION_EXPIRED_STATUS,
  SESSION_EXPIRED_MESSAGE,
  SESSION_KEY_LABEL,
  SortTypes,
  HOTEL_RECENT_SEARCHES_LABEL,
  DEAL_HOME_LABEL,
  ALL_INCLUSIVE_KEY,
} from '@share/constants';

import { getHeaders, axiosInstance, getSearchBody, UrlUtils, getTimeout, RootState, AppThunk, delay, getSelectedCurrency, hasChangedCurrency } from '@share/utils';

import { datesActions } from './dates';
import { roomsActions } from './rooms';
import { locationActions } from './locations';
import { filtersActions } from './filters';
import { NULL_VALUE } from '@constants';
import { getLocationByCode } from '@share/services';

const oneItem = 1;
const zeroItem = 0;
const reRenderNum = 0.000000001;
const nearByMinimun = 4;

export interface IHotelsState {
  hotels: IHotel[];
  similarHotels: IHotel[];
  mapHotels: IHotel[];
  selectedCompareHotels: IHotel[];
  isMapView: boolean;
  loading: boolean;
  loadingSimilar: boolean;
  mapLoading: boolean;
  loadingMore: boolean;
  loadingFilter: boolean;
  loadingQuick: boolean;
  error: string;
  sessionKey?: ISessionKey;
  quickSessionKey?: ISessionKey;  
  similarSessionKey?: ISessionKey;
  isSearch: boolean;
  isWidget: boolean;
  pageNumber: number;
  searchedLocation: string;
  lastSearchedLocation: ILocation;
  counters: ICounters | null | undefined;
  filterCounters: ICounters | null | undefined;
  quickCounters: ICounters | null | undefined;
  quickFilterCounters: ICounters | null | undefined;
  similarCounters: ICounters | null | undefined;
  mapCounters: ICounters;
  prevHotels: IHotel[];
  isSessionExpired: boolean;
  bounds: IBounds;
  boundsSimilar: IBounds;
  recentSearches: IHotelsRecentSearches[];
  errorRecentSearches: string;
  loadingRecentSearches: boolean;
  referenceNumber: string;
  selectedHotel: IHotel;
  agencyNumber: string;
  selectedHotelSearchClientCash: IClientCash;
}

const initialState: IHotelsState = {
  hotels: NULL_VALUE,
  similarHotels: NULL_VALUE,
  mapHotels: NULL_VALUE,
  selectedCompareHotels: [],
  isMapView: false,
  loading: false,
  loadingSimilar: false,
  loadingMore: false,
  mapLoading: false,
  loadingFilter: false,
  loadingQuick: false,
  error: '',
  isSearch: false,
  isWidget: false,
  pageNumber: DEFAULT_PAGE_NUMBER,
  searchedLocation: '',
  counters: null,
  filterCounters: null,
  quickCounters: null,
  quickFilterCounters: null,
  similarCounters: null,
  mapCounters: NULL_VALUE,
  lastSearchedLocation: NULL_VALUE,
  bounds: NULL_VALUE,
  boundsSimilar: NULL_VALUE,
  prevHotels: [],
  isSessionExpired: false,
  recentSearches: [],
  errorRecentSearches: '',
  loadingRecentSearches: false,
  referenceNumber: NULL_VALUE,
  selectedHotel: NULL_VALUE,
  agencyNumber: NULL_VALUE,
  selectedHotelSearchClientCash: NULL_VALUE,
};

const hotelsSlice = createSlice({
  name: 'hotels',
  initialState,
  reducers: {
    setIsSearch: (state: IHotelsState, { payload }: PayloadAction<boolean>) => {
      state.isSearch = payload;
    },
    setIsWidget: (state: IHotelsState, { payload }: PayloadAction<boolean>) => {
      state.isWidget = payload;
    },
    setBounds: (state: IHotelsState, { payload }: PayloadAction<IBounds>) => {
      state.bounds = payload;
    },
    setBoundsSimilar: (state: IHotelsState, { payload }: PayloadAction<IBounds>) => {
      state.boundsSimilar = payload;
    },
    setIsMapView: (state: IHotelsState, { payload }: PayloadAction<boolean>) => {
      state.isMapView = payload;
    },
    setLoading: (state: IHotelsState, { payload }: PayloadAction<boolean>) => {
      state.loading = payload;
    },
    setLoadingSimilar: (state: IHotelsState, { payload }: PayloadAction<boolean>) => {
      state.loadingSimilar = payload;
    },
    setLoadingQuick: (state: IHotelsState, { payload }: PayloadAction<boolean>) => {
      state.loadingQuick = payload;
    },
    setMapLoading: (state: IHotelsState, { payload }: PayloadAction<boolean>) => {
      state.mapLoading = payload;
    },
    setLastSearchedLocation: (state: IHotelsState, { payload }: PayloadAction<ILocation>) => {
      state.lastSearchedLocation = payload;
    },
    setSelectedHotelSearchClientCash: (state: IHotelsState, { payload }: PayloadAction<IClientCash>) => {
      state.selectedHotelSearchClientCash = payload;
    },
    setError: (state: IHotelsState, { payload }: PayloadAction<string>) => {
      state.error = payload;
    },
    setHotels: (
      state: IHotelsState,
      { payload }: PayloadAction<{ hotels: IHotel[]; isLoadMore: boolean; isquick?: boolean; }>,
    ) => {
      state.prevHotels = payload.hotels;
      state.selectedHotel = NULL_VALUE;
      if (payload.isLoadMore) {
        state.hotels = [...(state.hotels || []), ...payload.hotels];
      }else  if (payload.isquick && state.hotels.length) {
          if (!state.hotels || state.hotels.length === 0) {
            state.hotels = payload.hotels;
          } 
      else if (state.hotels.length) {
          state.hotels = [...payload.hotels,  ...state.hotels];
        }
      }else {
        state.hotels = payload.hotels;
      }
    },
    setSimilarHotels: ( state: IHotelsState, { payload }: PayloadAction<{ hotels: IHotel[]; isLoadMore: boolean }>) => {
      if (payload.isLoadMore) {
        state.similarHotels = [...(state.similarHotels || []), ...payload.hotels];
      } else {
        state.similarHotels = payload.hotels;
      }
    },
    setSelectedCompareHotels: ( state: IHotelsState, { payload }: PayloadAction<IHotel[]>) => {
      state.selectedCompareHotels = payload;
    },
    resetHotels: (state: IHotelsState) => {
      state.prevHotels = [];
      state.hotels = [];
      state.similarHotels = [];
      state.selectedHotel = NULL_VALUE;
      state.error = '';
      state.errorRecentSearches = '';
    },
    clearHotels: (state: IHotelsState) => {
      state.prevHotels = NULL_VALUE;
      state.hotels = NULL_VALUE;
      state.similarHotels = NULL_VALUE;
      state.selectedHotel = NULL_VALUE;
      state.selectedCompareHotels = [];
      state.error = '';
      state.errorRecentSearches = '';
    },
    setSessionKey: (state: IHotelsState, { payload }: PayloadAction<ISessionKey>) => {
      state.sessionKey = payload;
    },
    setQuickSessionKey: (state: IHotelsState, { payload }: PayloadAction<ISessionKey>) => {
      state.quickSessionKey = payload;
    },
    setReferenceNumber: (state: IHotelsState, { payload }: PayloadAction<string>) => {
      state.referenceNumber = payload;
    },
    setAgencyNumber: (state: IHotelsState, { payload }: PayloadAction<string>) => {
      state.agencyNumber = payload;
    },
    setSimilarSessionKey: (state: IHotelsState, { payload }: PayloadAction<ISessionKey>) => {
      state.similarSessionKey = payload;
    },
    setLoadingMore: (state: IHotelsState, { payload }: PayloadAction<boolean>) => {
      state.loadingMore = payload;
    },
    setLoadingFilter: (state: IHotelsState, { payload }: PayloadAction<boolean>) => {
      state.loadingFilter = payload;
    },
    setPageNumber: (state: IHotelsState, { payload }: PayloadAction<number | undefined>) => {
      state.pageNumber = payload ? payload : state.pageNumber + oneItem;
    },
    setSearchedLocation: (state: IHotelsState, { payload }: PayloadAction<string>) => {
      state.searchedLocation = payload;
    },
    setCounters: (state: IHotelsState, { payload }: PayloadAction<ICounters>) => {
      state.counters = payload;
    },
    setFilterCounters: (state: IHotelsState, { payload }: PayloadAction<ICounters | null>) => {
      state.filterCounters = payload;
    },
    setQuickCounters: (state: IHotelsState, { payload }: PayloadAction<ICounters>) => {
      state.quickCounters = payload;
    },
    setQuickFilterCounters: (state: IHotelsState, { payload }: PayloadAction<ICounters | null>) => {
      state.quickFilterCounters = payload;
    },
    setSimilarCounters: (state: IHotelsState, { payload }: PayloadAction<ICounters>) => {
      state.similarCounters = payload;
    },
    setMapCounters: (state: IHotelsState, { payload }: PayloadAction<ICounters>) => {
      state.mapCounters = payload;
    },
    setMapHotels: (state: IHotelsState, { payload }: PayloadAction<IHotel[]>) => {
      state.mapHotels = payload;
    },
    setIsSessionExpired: (state: IHotelsState, { payload }: PayloadAction<boolean>) => {
      state.isSessionExpired = payload;
    },
    setSelectedHotel: (state: IHotelsState, { payload }: PayloadAction<IHotel>) => {
      state.selectedHotel = payload;
    },
    setRecentSearches: (
      state: IHotelsState,
      { payload }: PayloadAction<IHotelsState['recentSearches']>,
    ) => {
      state.recentSearches = payload;
    },
    setLoadingRecentSearches: (state: IHotelsState, { payload }: PayloadAction<boolean>) => {
      state.loadingRecentSearches = payload;
    },
    setErrorRecentSearches: (state: IHotelsState, { payload }: PayloadAction<string>) => {
      state.errorRecentSearches = payload;
    },
  },
});

export const hotelsActions = hotelsSlice.actions;

export const hotelsReducer = hotelsSlice.reducer;

let cancelRequest: Canceler;
let expirationTimer: number;

export const setSessionExpirationTimer = (
  session: ISessionKey,
  dispatch: ThunkDispatch<RootState, unknown, Action<any>>,
): void => {
  if (expirationTimer) {
    clearTimeout(expirationTimer);
  }

  // @ts-ignore
  expirationTimer = setTimeout(() => {
    dispatch(hotelsActions.setIsSessionExpired(true));
  }, getTimeout(session?.expireDate));
};

export const resetHotels = (): AppThunk => {
  return async (dispatch, getState) => {
    const { hotelsStore } = getState();

    if (!hotelsStore.isMapView) {
      dispatch(hotelsActions.setLoading(true));
    }
    dispatch(hotelsActions.resetHotels());    
  };
};

export const resetHotelsFullAshevillePlayLocation = (): AppThunk => {
  return async (dispatch, getState) => {
    const { loginStore } = getState();
    const { account } = loginStore;

    const isAshevillePlay = account?.isAshevillePlay;
    const ashevillePlayLocationCode = account?.ashevillePlayLocationCode;

    if (isAshevillePlay && ashevillePlayLocationCode) {
      try {
        const response = await getLocationByCode(ashevillePlayLocationCode, true);
  
        if (!!response) {
          const location = response.data;    
          dispatch(locationActions.setSelectLocationLabel(location.name as string));
          dispatch(locationActions.setSelectLocation(location as ILocation));
        }
      } catch(e) {
        console.error(e);
      }
    } else {
      dispatch(locationActions.resetLocation());
    }
  };
};

export const resetHotelsFull = (): AppThunk => {
  return async (dispatch, getState) => {
    const { loginStore } = getState();
    const { account } = loginStore;

    if (account?.isAshevillePlay) {
      dispatch(resetHotelsFullAshevillePlayLocation());
    } else {
      dispatch(locationActions.resetLocation());
    }
    dispatch(datesActions.resetDates());
    dispatch(locationActions.setLocationKey(new Date().getTime().toString()));
    dispatch(roomsActions.resetRooms());
    dispatch(filtersActions.resetFilters());    
    dispatch(hotelsActions.setIsSearch(false));
    dispatch(hotelsActions.clearHotels());    
    dispatch(hotelsActions.setError(''));    
    dispatch(hotelsActions.setErrorRecentSearches(''));    
  };
};

export const resetHotelsFullWithParams = (): AppThunk => {
  return async (dispatch) => {
    dispatch(resetHotelsFull());
    dispatch(locationActions.applyLocation());
    dispatch(roomsActions.applyRooms());
    dispatch(datesActions.applyDates());    
  };
};

export const setHotelsFull = (hotels: IHotel[]): AppThunk => {
  return async (dispatch) => {
    dispatch(hotelsActions.setHotels({ hotels: hotels, isLoadMore: false }));    
  };
};

export const updateSavedProperty = (propertyId: number, added: boolean): AppThunk => {
  return async (dispatch, getState) => {

    const { hotelsStore } = getState();
    const { hotels } = hotelsStore;
      
    if (hotels?.length) {
      const clonedHotels = hotels.map((o: any) => ({ ...o, savedProperty: o.hotelId == propertyId ? added : o.savedProperty }))
      dispatch(hotelsActions.setHotels({ hotels: clonedHotels, isLoadMore: false }));    
    }
  };
};

const getHotelsCall = (locationToSearch: ILocation, isLoadMore: boolean, isReuseSessionKey: boolean, getState: any, quick: boolean) => {
  const { datesStore, roomsStore, loginStore } = getState();
  const { startDate, endDate } = datesStore;
  const { rooms } = roomsStore;
  const { lifeStyle } = loginStore;

  return axiosInstance.post(
    Urls.Hotels,
    getSearchBody(
      locationToSearch,
      startDate,
      endDate,
      rooms,
      getState().hotelsStore,
      getState().filtersStore,
      isLoadMore,
      hasChangedCurrency() ? false : isReuseSessionKey,
      NULL_VALUE,
      quick,
      getSelectedCurrency(loginStore.account),
      lifeStyle,
      loginStore
    ),
    {
      ...getHeaders(),
      cancelToken: new axios.CancelToken((canceler: Canceler) => {
        cancelRequest = canceler;
      }),
    },
  );
}

const getHotelsRetry = async (locationToSearch: ILocation, isLoadMore: boolean, isReuseSessionKey: boolean, getState: any, quick: boolean) => {
  try {
    return await getHotelsCall(locationToSearch, isLoadMore, isReuseSessionKey, getState, quick);
  } catch (error) {
    await delay(300);
    return await getHotelsCall(locationToSearch, isLoadMore, isReuseSessionKey, getState, quick);
  }
}

export const setLoadingSimilar = (dispatch: any, isLoadMore: boolean, loading: boolean) => {
  if (isLoadMore) {
    dispatch(hotelsActions.setLoadingMore(loading));
  } else {
    dispatch(hotelsActions.setLoadingSimilar(loading));
  }
}

export const getSimilarHotelsBase = (
  isFiltersChange: boolean,
  isLoadMore: boolean,
  type: string
): AppThunk => {
  return async (dispatch, getState) => {

    const { locationsStore, loginStore } = getState();
    const { account } = loginStore;
    const { location } = locationsStore;

    const locationToSearch = { ...location, type } ;
    
    setLoadingSimilar(dispatch, isLoadMore, true);

    try {
      const response = getHotelsRetry(locationToSearch, isLoadMore, false, getState, false);
      response.then(res => {
        const enableMinProfit = account?.enableMinProfit;
        const minProfit = account?.minProfit;
    
        const isMinProfit = enableMinProfit && !!minProfit && minProfit >= 0;
    
        const isSearchSecondaryHotels = type === 'Related' && 
                                              location?.type === 'Hotel' && 
                                              res.data.searchSecondaryHotels?.length > 0 &&
                                              isMinProfit;

        const locationCode = location?.code?.toString(); 
        const searchHotels = res.data.searchHotels;
        const searchSecondaryHotels = res.data.searchSecondaryHotels;

        const hotels = searchHotels.filter((h: any) => h?.hotelId?.toString() !== locationCode);
        const hasSelectedSecondaryHotel = isSearchSecondaryHotels ? 
                                                searchSecondaryHotels?.some((h: any) => h?.hotelId?.toString() === locationCode) : 
                                                false;
        
        if (res.data.counters) {
          dispatch(hotelsActions.setSimilarCounters(res.data.counters));
        }
        
        if (res.data.bounds) {
          dispatch(hotelsActions.setBoundsSimilar(res.data.bounds));
        }
                
        if (res.data?.sessionKey?.isInvalid) {
          dispatch(hotelsActions.setSimilarSessionKey(NULL_VALUE));
        }
        
        if (res.data.searchHotels?.length) {
          dispatch(hotelsActions.setSimilarSessionKey(res.data.sessionKey));
        }
        
        if (isFiltersChange) {
          dispatch(hotelsActions.setSimilarHotels({ hotels, isLoadMore: false }));
        } else {
          dispatch(hotelsActions.setSimilarHotels({ hotels, isLoadMore }));
        }

        if (hasSelectedSecondaryHotel) {
          dispatch(hotelsActions.setHotels({ hotels: res.data.searchSecondaryHotels, isLoadMore: false }));
        }

        setLoadingSimilar(dispatch, false, false);
        dispatch(hotelsActions.setLoadingMore(false));
      }).catch((error) => {
        console.error(error);
        dispatch(hotelsActions.setMapLoading(false));

        setLoadingSimilar(dispatch, false, false);  
        dispatch(hotelsActions.setLoadingMore(false));
      });
    } catch (error) {
      console.error(error);
      dispatch(hotelsActions.setMapLoading(false));
      dispatch(hotelsActions.setLoadingMore(false));

      setLoadingSimilar(dispatch, false, false);
    }

  };
}

export const getSimilarHotels = (
  isFiltersChange: boolean,
  isLoadMore: boolean
): AppThunk => {
  return async (dispatch) => {
    dispatch(getSimilarHotelsBase(isFiltersChange, isLoadMore, 'Related'));
  };
}

export const getSimilarHotelsNearBy = (
  isFiltersChange: boolean,
  isLoadMore: boolean
): AppThunk => {
  return async (dispatch) => {
    dispatch(getSimilarHotelsBase(isFiltersChange, isLoadMore, 'ShowMoreNearbyOptions'));
  };
}

export const initializeSearch = (): AppThunk => {
  return async (dispatch) => {
    dispatch(hotelsActions.setIsSearch(true));
    dispatch(hotelsActions.setLoading(true));
    dispatch(hotelsActions.setMapLoading(true));
    dispatch(hotelsActions.setHotels({ hotels: [], isLoadMore: false }));
  }
}

const updateMergedCounters = (dispatch: any, getState: any) => {
  const { hotelsStore } = getState();
  const { counters, quickCounters } = hotelsStore;
  if (quickCounters && counters) {
    if((quickCounters.totalFilteredHotels>0) && (counters.totalFilteredHotels>0)){
     const MergeResult = mergeQuickFullCounter(quickCounters, counters);
     dispatch( hotelsActions.setCounters( MergeResult as ICounters ) );
    }else if(quickCounters.totalFilteredHotels>0){
       dispatch(hotelsActions.setCounters(quickCounters));
    }else if(counters.totalFilteredHotels>0){
        dispatch(hotelsActions.setCounters(counters));
    }else{
        dispatch(hotelsActions.setCounters(counters));
    }
  } else if (counters) {
    dispatch(hotelsActions.setCounters(counters));
  } else if (quickCounters) {
    dispatch(hotelsActions.setCounters(quickCounters));
  }
};

const updateFilterMergedCounters = (dispatch: any, getState: any) => {
  const { hotelsStore } = getState();
  const { filterCounters, quickFilterCounters } = hotelsStore;
  if (quickFilterCounters && filterCounters) {
   if((quickFilterCounters.totalFilteredHotels>0) && (filterCounters.totalFilteredHotels>0)){
     const MergeResult = mergeQuickFullCounter(quickFilterCounters, filterCounters);
     dispatch( hotelsActions.setFilterCounters( MergeResult as ICounters ) );
    }else if(quickFilterCounters.totalFilteredHotels>0){
       dispatch(hotelsActions.setFilterCounters(quickFilterCounters));
    }else if(filterCounters.totalFilteredHotels>0){
        dispatch(hotelsActions.setFilterCounters(filterCounters));
    }else{
        dispatch(hotelsActions.setFilterCounters(filterCounters));
    }
  } else if (filterCounters) {
    dispatch(hotelsActions.setFilterCounters(filterCounters));
  } else if (quickFilterCounters) {
    dispatch(hotelsActions.setFilterCounters(quickFilterCounters));
  }
};

let filterHotel: IHotel[] | null;

const mergeFilterHotel = (
  hotels: IHotel[],
  isQuick: boolean = false
): IHotel[] => {
  let result: IHotel[];

  if (!filterHotel || !filterHotel.length) {
    result = [...hotels];
  } else if (isQuick && hotels.length) {
    result = [...hotels, ...filterHotel];
  } else {
    result = [...filterHotel, ...hotels];
  }

  filterHotel = result;

  return result;
};

const normalizeKey = (key: any): string => typeof key === 'object'  ? JSON.stringify(key)  : String(key).toLowerCase();


const mergeKeyCountArray = (arr1: any[] = [], arr2: any[] = []) => {
  const map = new Map<string, any>();
  [...arr1, ...arr2].forEach(item => {
    const key = normalizeKey(item.key);
    const existing = map.get(key);

    if (!existing) {
      map.set(key, { ...item });
      return;
    }
    const existingDisabled = existing.disabled === true;
    const currentDisabled = item.disabled === true;

    if (existingDisabled && !currentDisabled) {
      map.set(key, { ...item });
      return;
    }

    if (!existingDisabled && currentDisabled) {
      return;
    }
    existing.count += item.count;
    map.set(key, existing);
  });
  return Array.from(map.values());
};

export const mergeQuickFullCounter = (QuickCounter: ICounters, FullCounter: ICounters) => {
  return {
    amenities: mergeKeyCountArray(QuickCounter.amenities, FullCounter.amenities),
    accessibilities: mergeKeyCountArray(QuickCounter.accessibilities, FullCounter.accessibilities),
    budgetRanges: mergeKeyCountArray(QuickCounter.budgetRanges, FullCounter.budgetRanges),
    propertyTypes: mergeKeyCountArray(QuickCounter.propertyTypes, FullCounter.propertyTypes),
    boardNames: mergeKeyCountArray(QuickCounter.boardNames, FullCounter.boardNames),
    refundable: mergeKeyCountArray(QuickCounter.refundable, FullCounter.refundable),
    starRating: mergeKeyCountArray(QuickCounter.starRating, FullCounter.starRating),

    neighbourhoods: mergeKeyCountArray(QuickCounter.neighbourhoods, FullCounter.neighbourhoods),

    totalHotels: (QuickCounter.totalHotels || 0) + (FullCounter.totalHotels || 0),
    totalFilteredHotels:
      (QuickCounter.totalFilteredHotels || 0) + (FullCounter.totalFilteredHotels || 0),
    totalNights: (QuickCounter.totalNights || 0) + (FullCounter.totalNights || 0),
    totalFlexibleDates:
      (QuickCounter.totalFlexibleDates || 0) + (FullCounter.totalFlexibleDates || 0),
  };
};

export const mergeCounters = (counters: any, filterCounter: any, filterCountersUpcomming: any, isquick: boolean): AppThunk => {
  return async (dispatch, getState) => {
    const countersMerge = filterCounter ? { ...filterCounter } : counters ? { ...counters } : null;
    if (filterCountersUpcomming && countersMerge) {
      Object.keys(filterCountersUpcomming).forEach(key => {
        const typedKey = key as keyof typeof filterCountersUpcomming;
        const counterKey = key as keyof typeof countersMerge;

        if (countersMerge?.hasOwnProperty(key)) {
          if (Array.isArray(countersMerge[counterKey])) {
            const filteredItemsCounters = filterCountersUpcomming?.[typedKey];
            const filteredItems = typedKey !== 'budgetRanges' ? 
                                      filteredItemsCounters : 
                                      filterCountersUpcomming?.[typedKey]?.filter((item: any) => !!item.count);

            countersMerge[counterKey] = [ 
              ...countersMerge[counterKey]
                  .filter((item: any) => !filteredItems?.map((f: any) => JSON.stringify(f.key)).includes(JSON.stringify(item.key)))
                  .map((item: any) => ({ ...item, disabled: true })), 
              ...filteredItems
            ];

            if (countersMerge[counterKey].length > 5) {
              countersMerge[counterKey].sort((a: any, b: any) => {
                if (a.disabled && !b.disabled) {
                  return 1;
                }
                if (!a.disabled && b.disabled) {
                  return -1;
                }
          
                if (!a.disabled && !b.disabled) {
                  return 0;
                }
          
                return 0;
              });
            }
        
          } else {
            countersMerge[counterKey] = filterCountersUpcomming[typedKey];
          }
        } else {
          countersMerge[counterKey] = filterCountersUpcomming[typedKey];
        }
      });
       
        if (isquick) {
          dispatch(hotelsActions.setQuickCounters(filterCountersUpcomming));
          dispatch(hotelsActions.setQuickFilterCounters(countersMerge));
        } else {
           dispatch(hotelsActions.setCounters(filterCountersUpcomming));
           dispatch(hotelsActions.setFilterCounters(countersMerge));
        }
        updateMergedCounters(dispatch, getState);  
        updateFilterMergedCounters(dispatch, getState);

    } else {
      dispatch(hotelsActions.setCounters(filterCountersUpcomming));
      updateMergedCounters(dispatch, getState);  
      dispatch(hotelsActions.setFilterCounters(null));
    }
  }
}

export const getHotels = (
  lastSearchedLocation?: ILocation,
  isLoadMore = false,
  isFiltersChange = false,
  isReuseSessionKey = false,
): AppThunk => {
  return async (dispatch, getState) => {
    const { hotelsStore } = getState();
    const { counters, filterCounters ,quickCounters, quickFilterCounters} = hotelsStore;
    /*
    dispatch(hotelsActions.setCounters(NULL_VALUE));
    dispatch(hotelsActions.setFilterCounters(NULL_VALUE));
    dispatch(hotelsActions.setQuickCounters(NULL_VALUE));
    dispatch(hotelsActions.setQuickFilterCounters(NULL_VALUE));
    */
    dispatch(hotelsActions.setSelectedHotel(NULL_VALUE));
    dispatch(hotelsActions.setError(''));    
    dispatch(hotelsActions.setErrorRecentSearches(''));    
    filterHotel = null;

    if (!isLoadMore) {
      dispatch(datesActions.applyDates());
      dispatch(roomsActions.applyRooms());
      dispatch(locationActions.applyLocation());
      dispatch(hotelsActions.setSelectedCompareHotels([]))
      dispatch(hotelsActions.setHotels({ hotels: [], isLoadMore: false }));
    }
    
    dispatch(hotelsActions.setSimilarHotels({ hotels: [], isLoadMore: false }));
    dispatch(hotelsActions.setSimilarCounters(NULL_VALUE));
    dispatch(hotelsActions.setSimilarSessionKey(NULL_VALUE));

    // initial setting hotels to show skeleton
    if (!hotelsStore.hotels) {
      dispatch(hotelsActions.setHotels({ hotels: [], isLoadMore: false }));
    }
    const { locationsStore, loginStore } = getState();
    const { location } = locationsStore;
    
    const locationToSearch = lastSearchedLocation ? lastSearchedLocation : location;

    let currentPage = hotelsStore.pageNumber;
    
    dispatch(hotelsActions.setSearchedLocation(locationToSearch.name));
    dispatch(hotelsActions.setLastSearchedLocation(locationToSearch));

    // cancel request if it's not pagination request
    if (cancelRequest) {
      cancelRequest();
    }

    let loadQuick = loginStore.account.hasQuick;
    //const isB2C = loginStore.account.isB2C;
    //const walletWalletSavings = loginStore.account.walletWalletSavings;

    const values = UrlUtils.getValues();
    if (values[DEAL_HOME_LABEL] && (values[DEAL_HOME_LABEL] as string) === ALL_INCLUSIVE_KEY && !isFiltersChange && !isLoadMore) {
      dispatch(filtersActions.setSortBy(SortTypes.PriceAsc));
    } 
    /*
    else {
      if (!isB2C || walletWalletSavings) {
        if (!loadQuick && !isFiltersChange && !isLoadMore) {
          //dispatch(filtersActions.setSortBy(SortTypes.BiggestSavingsPercentage));
        }
      } else {
        if (!loadQuick && !isFiltersChange && !isLoadMore) {
          dispatch(filtersActions.setSortBy(SortTypes.GuestRating));
        }
      }
    }
    */

    if (isLoadMore) {
      dispatch(hotelsActions.setLoadingMore(true));
      loadQuick = false;
    } else {
      dispatch(hotelsActions.setMapLoading(true));
      dispatch(hotelsActions.setLoading(true));
      dispatch(hotelsActions.setIsSearch(true));
      dispatch(hotelsActions.setLoadingFilter(false));
      dispatch(hotelsActions.setPageNumber(DEFAULT_PAGE_NUMBER));
      currentPage = DEFAULT_PAGE_NUMBER;
    }

    if (isFiltersChange) {
      dispatch(hotelsActions.setPageNumber(DEFAULT_PAGE_NUMBER));
      dispatch(hotelsActions.setLoadingFilter(true));
      currentPage = DEFAULT_PAGE_NUMBER;
      loadQuick = false;
    }

    if (location?.type === 'Hotel') {
      loadQuick = false;
    }

    try {
      const { datesStore, roomsStore, filtersStore } = getState();
      const { startDate, endDate } = datesStore;
      const { searchHomes } = filtersStore;
      const { rooms } = roomsStore;

      const siteId = loginStore?.user?.userAdditionalData?.siteID;
      const organizationId = loginStore?.user?.userAdditionalData?.organizationId;
      const account = loginStore?.account as IAccount;

      const toNumberArray = (val?: string): number[] =>val?.split(',').map(v => Number(v.trim())).filter(Boolean) ?? [];

      const orgIds = toNumberArray(GetPreferenceValue(account, 'quick_orgIds'));
      const siteIds = toNumberArray(GetPreferenceValue(account, 'quick_siteIds'));

      const QuickEnable = orgIds.length && siteIds.length
          ? orgIds.includes(organizationId) && siteIds.includes(siteId)
          : orgIds.length
          ? orgIds.includes(organizationId)
          : siteIds.length
          ? siteIds.includes(siteId)
          : true;


      if (loadQuick && QuickEnable) {
        if (!isLoadMore) {
          dispatch(hotelsActions.setLoadingQuick(true));
        }

        const requestOne = getHotelsRetry(locationToSearch, isLoadMore, isReuseSessionKey, getState, true);
        requestOne.then(resQuick => {
     
          if (resQuick.data.bounds) {
            dispatch(hotelsActions.setBounds(resQuick.data.bounds));
          } else if (!resQuick.data.searchHotels?.length) {
            dispatch(hotelsActions.setBounds({...hotelsStore?.bounds, northEast: {...hotelsStore?.bounds?.northEast,latitude: hotelsStore?.bounds?.northEast.latitude + reRenderNum,},}),);
          }

          if (resQuick.data.counters) {
            if (isFiltersChange) {
              dispatch(mergeCounters(quickCounters, quickFilterCounters, resQuick.data.counters ,true));
            } else {
              dispatch(hotelsActions.setQuickCounters(resQuick.data.counters));
              updateMergedCounters(dispatch,getState);
              dispatch(hotelsActions.setFilterCounters(null));
            }
          }
          
          if (resQuick.data.searchHotels.length>0 ) {
            if (isFiltersChange) {
              const HotelList = mergeFilterHotel(resQuick.data.searchHotels,true );
              dispatch(hotelsActions.setHotels({ hotels: HotelList, isLoadMore: false }));
            } else {
              dispatch(hotelsActions.setHotels({ hotels: resQuick.data.searchHotels, isLoadMore, isquick: true }));
            }
          }

          if (resQuick.data?.sessionKey?.isInvalid) {
            dispatch(hotelsActions.setQuickSessionKey(NULL_VALUE));
            UrlUtils.removeFromUrl(SESSION_KEY_LABEL);
            dispatch(getHotels(locationToSearch));
            return;
          }
          dispatch(hotelsActions.setQuickSessionKey(resQuick.data.sessionKey)); 

          if (resQuick.data.searchHotels.length > 0){
            dispatch(hotelsActions.setLoading(false));
            dispatch(hotelsActions.setLoadingMore(false));
          }

        }).catch((error) => {
          handleGetHotelsErrors(error, dispatch);
        });
      }

   
      const requestTwo = getHotelsRetry(locationToSearch, isLoadMore, isReuseSessionKey, getState, false);
      requestTwo.then(resFull => {
      	const recentSearchesStorage = localStorage.getItem(HOTEL_RECENT_SEARCHES_LABEL);
        const recentSearchesObject = (recentSearchesStorage && !isEmpty(recentSearchesStorage)) ? JSON.parse(recentSearchesStorage) : [];
        const recentSearchesFiltered = recentSearchesObject?.length ? recentSearchesObject.filter((search: IHotelsRecentSearches) => moment().startOf('day').isSameOrBefore(moment(search.checkIn, 'yyyy-MM-DD'))) : [];
        const recentSearchesValid = recentSearchesFiltered.length > 20 ? recentSearchesFiltered.slice(0, 19) : recentSearchesFiltered;
        const currentSearch: any = {
          checkIn: moment(startDate, 'yyyy-MM-DD').format('yyyy-MM-DDT00:00:00'),
          checkOut: moment(endDate, 'yyyy-MM-DD').format('yyyy-MM-DDT00:00:00'),
          location: {
            ...locationToSearch,
            type: locationToSearch?.realType || locationToSearch?.type,
            realType: undefined,
          },
          locationCode: locationToSearch.code,
          type: searchHomes,
          rooms: [...rooms.map((r: any) => ({
            adultsCount: r.adultsCount,
            kids: r.kids
          }))]
        };
        
        const currentSearchStr = JSON.stringify(currentSearch);
        if (!recentSearchesValid.map((r: any) => JSON.stringify(r)).includes(currentSearchStr)) {
          localStorage.setItem(HOTEL_RECENT_SEARCHES_LABEL, JSON.stringify([currentSearch, ...recentSearchesValid]));
        }

        if (resFull.data.bounds) {
          dispatch(hotelsActions.setBounds(resFull.data.bounds));
        } else if (!resFull.data.searchHotels?.length) {
          if (locationToSearch?.geoLocation?.latitude && locationToSearch?.geoLocation?.longitude) {
            dispatch(
              hotelsActions.setBounds({
                northEast: {
                  latitude: locationToSearch.geoLocation.latitude + reRenderNum,
                  longitude: locationToSearch.geoLocation.longitude + reRenderNum,
                },
                southWest: {
                  latitude: locationToSearch.geoLocation.latitude + reRenderNum,
                  longitude: locationToSearch.geoLocation.longitude + reRenderNum,
                },
              }),
            );
          } else {
            dispatch(hotelsActions.setBounds(NULL_VALUE));
          }
        }

        if (resFull.data?.sessionKey?.isInvalid) {
          dispatch(hotelsActions.setSessionKey(NULL_VALUE));
          UrlUtils.removeFromUrl(SESSION_KEY_LABEL);
          dispatch(getHotels(locationToSearch));
          return;
        }

        dispatch(hotelsActions.setSessionKey(resFull.data.sessionKey));

        if (resFull.data.sessionKey) {
          dispatch(hotelsActions.setIsSessionExpired(false));
          // @ts-ignore
          setSessionExpirationTimer(resFull.data.sessionKey, dispatch);
        }

        if (resFull.data.counters) {
          dispatch(hotelsActions.setCounters(resFull.data.counters));
          if (isFiltersChange) {
            dispatch(mergeCounters(counters, filterCounters, resFull.data.counters, false));
          } else {
            updateMergedCounters(dispatch,getState);
            dispatch(hotelsActions.setFilterCounters(null));
          }
        }

        if (resFull.data.searchHotels.length>0) {
          if (isFiltersChange) {
              const HotelList = mergeFilterHotel(resFull.data.searchHotels, false);
              dispatch(hotelsActions.setHotels({ hotels: HotelList, isLoadMore: false }));
          } else {
            dispatch(hotelsActions.setHotels({ hotels: resFull.data.searchHotels, isLoadMore: loadQuick ? true : isLoadMore }));
          }
        }
        
        if (location?.type === 'Hotel') {
          dispatch(hotelsActions.setLoadingSimilar(true));
          dispatch(getSimilarHotels(isFiltersChange, isLoadMore));
        } else {
          if (resFull.data.counters?.totalHotels <= nearByMinimun && !account.disableNearbyResults) {
            dispatch(hotelsActions.setLoadingSimilar(true));
            dispatch(getSimilarHotelsNearBy(isFiltersChange, isLoadMore));
          }
        }

        dispatch(hotelsActions.setLoading(false));
        dispatch(hotelsActions.setMapLoading(false));
        dispatch(hotelsActions.setLoadingMore(false));
        dispatch(hotelsActions.setLoadingFilter(false));
        dispatch(hotelsActions.setLoadingQuick(false));

      }).catch((error) => {
        handleGetHotelsErrors(error, dispatch);
      });

    } catch (error) {
      handleGetHotelsErrors(error, dispatch);
    }
  };
};

const handleGetHotelsErrors = (error: any, dispatch: any) => {
  console.error(error);

  dispatch(hotelsActions.setMapLoading(false));
  dispatch(hotelsActions.setError(
                    !isEmpty(error?.response?.data?.code) ? 
                          error?.response?.data?.code?.toString() : 
                          !isEmpty(error?.code) ? 
                                error?.code?.toString() : 
                                'GENERIC'));

  // error when user session ended
  if (
    error?.response?.status === SESSION_EXPIRED_STATUS &&
    error?.response?.data &&
    error?.response?.data[zeroItem] === SESSION_EXPIRED_MESSAGE
  ) {
    dispatch(hotelsActions.setIsSessionExpired(true));
  }

  // error triggered by axios when we cancel request
  if (!(error instanceof axios.Cancel)) {
    dispatch(hotelsActions.setLoadingMore(false));
    dispatch(hotelsActions.setLoading(false));
  }
  
  dispatch(hotelsActions.setLoadingFilter(false));
  dispatch(hotelsActions.setLoadingQuick(false));
}

let cancelMapRequest: Canceler;

const getHotelsMapCall = async (data: any) => {  
  return await axiosInstance.post(Urls.HotelsMap, data, {
    ...getHeaders(),
    cancelToken: new axios.CancelToken((canceler: Canceler) => {
      cancelMapRequest = canceler;
    }),
  });
}

const getHotelsMapRetry = async (data: any) => {
  try {
    return await getHotelsMapCall(data);
  } catch (error) {
    await delay(300);
    return await getHotelsMapCall(data);
  }
}

export const getMapHotels = (
  location: ILocation,
  startDate: string | null | undefined,
  endDate: string | null | undefined,
  rooms: IRoom[],
  hotelId: number | null = null,
): AppThunk => {
  return async (dispatch, getState) => {
    if (cancelMapRequest) {
      cancelMapRequest();
    }

    dispatch(hotelsActions.setSelectedHotel(NULL_VALUE));
    dispatch(hotelsActions.setError(''));    
    dispatch(hotelsActions.setErrorRecentSearches(''));    

    dispatch(hotelsActions.setMapHotels([]));
    dispatch(hotelsActions.setMapLoading(true));

    const { hotelsStore, loginStore } = getState();
    const { lifeStyle } = loginStore;

    try {
      const options = getSearchBody(
        location,
        startDate,
        endDate,
        rooms,
        getState().hotelsStore,
        getState().filtersStore,
        false,
        false,
        hotelId,
        false,
        getSelectedCurrency(loginStore.account),
        lifeStyle,
        loginStore
      );
      const { sessionKey } = hotelsStore;
      const data = {
        ...options,
        limit: DEFAULT_MAP_LIMIT,
        bounds: hotelsStore.bounds,
      };

      delete data.pageNumber;
      delete data.pageSize;

      data.sessionKey = sessionKey;

      const res: { data: IHotelsResponse } = await getHotelsMapRetry(data);

      if (res.data.bounds && hotelId) {
        dispatch(hotelsActions.setBounds(res.data.bounds));
      }

      dispatch(hotelsActions.setMapLoading(false));

      if (res.data.counters) {
        dispatch(hotelsActions.setMapCounters(res.data.counters));
      }

      dispatch(hotelsActions.setMapHotels(res.data.searchHotels));
    } catch (error: any) {
      console.error(error);
      dispatch(hotelsActions.setMapLoading(false));
      dispatch(hotelsActions.setError(!isEmpty(error?.code) ? error?.code?.toString() : 'GENERIC'));

      if (
        error?.response?.status === SESSION_EXPIRED_STATUS &&
        error?.response?.data &&
        error?.response?.data[zeroItem] === SESSION_EXPIRED_MESSAGE
      ) {
        dispatch(hotelsActions.setIsSessionExpired(true));
      }
    }
  };
};

export const getHotelsRecentSearches = (): AppThunk => {
  return async (dispatch) => {
  try {
      dispatch(hotelsActions.setLoadingRecentSearches(true));
      dispatch(hotelsActions.setErrorRecentSearches(''));    
  
      const recentSearchesStorage = localStorage.getItem(HOTEL_RECENT_SEARCHES_LABEL);
      const recentSearches = (recentSearchesStorage && !isEmpty(recentSearchesStorage)) ? JSON.parse(recentSearchesStorage) : null;
      const recentSearchesFiltered = recentSearches? recentSearches.filter((search: IHotelsRecentSearches) => moment().startOf('day').isSameOrBefore(moment(search.checkIn, 'yyyy-MM-DD'))) : [];

      dispatch(hotelsActions.setRecentSearches(recentSearchesFiltered));
      dispatch(hotelsActions.setLoadingRecentSearches(false));
    } catch (error: any) {
      console.error(error);
      dispatch(hotelsActions.setErrorRecentSearches(error?.code?.toString()));
      dispatch(hotelsActions.setLoadingRecentSearches(false));
    }
  };
};
